import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import client, { apiError } from './adminApi';
import { useAdminAuth, ROLE_LABELS } from './AdminAuth';
import { ConfirmDialog } from './components/ui';

const inputCls = 'mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-navy-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900';

// User management — Owner only. No public registration exists; accounts are
// created here. Passwords must be 10+ characters and are never displayed.
export default function UsersPage() {
  const { user: me } = useAdminAuth();
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', role: 'editor', password: '' });
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const deleteUser = async () => {
    if (!toDelete) return;
    try {
      await client.delete(`/admin/users/${toDelete.userId}`);
      setNotice(`Account ${toDelete.email} permanently deleted.`);
      setToDelete(null);
      await load();
    } catch (err) {
      setToDelete(null);
      setError(apiError(err, 'Could not delete the account.'));
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await client.get('/admin/users');
      setUsers(res.data.users);
    } catch (err) {
      setError(apiError(err, 'Users could not be loaded.'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await client.post('/admin/users', form);
      setNotice(`Account created for ${form.email}. Share the password securely — it is never shown again.`);
      setForm({ name: '', email: '', role: 'editor', password: '' });
      await load();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const setRole = async (userId, role) => {
    try {
      await client.patch(`/admin/users/${userId}`, { role });
      await load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const toggleStatus = async (u) => {
    try {
      await client.patch(`/admin/users/${u.userId}`, { status: u.status === 'active' ? 'disabled' : 'active' });
      await load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div data-testid="admin-users">
      <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Users</h1>
      <p className="mt-1 text-sm text-slate-500">Administration accounts and roles. There is no public registration.</p>

      {notice && <p role="status" className="mt-4 rounded-md border border-teal-200 bg-teal-50 px-4 py-2.5 text-sm font-semibold text-teal-800" data-testid="users-notice">{notice}</p>}
      {error && <p role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700" data-testid="users-error">{error}</p>}

      {!users ? (
        <div className="flex justify-center py-16" role="status"><Loader2 className="h-6 w-6 animate-spin text-navy-900" /></div>
      ) : (
        <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                <th scope="col" className="px-4 py-3">Name</th>
                <th scope="col" className="px-4 py-3">Email</th>
                <th scope="col" className="px-4 py-3">Role</th>
                <th scope="col" className="px-4 py-3">Status</th>
                <th scope="col" className="px-4 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.userId} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 font-semibold text-navy-900">{u.name}{u.userId === me.userId && ' (you)'}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <select
                      value={u.role} onChange={(e) => setRole(u.userId, e.target.value)}
                      disabled={u.userId === me.userId}
                      aria-label={`Role for ${u.email}`} data-testid={`user-role-${u.userId}`}
                      className="rounded-md border border-slate-300 px-2 py-1.5 text-xs font-bold"
                    >
                      {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-xs font-bold uppercase text-slate-600">{u.status}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      {u.userId !== me.userId && (
                        <button type="button" onClick={() => toggleStatus(u)} data-testid={`user-toggle-${u.userId}`} className="text-xs font-bold text-navy-900 underline-offset-2 hover:underline">
                          {u.status === 'active' ? 'Disable' : 'Enable'}
                        </button>
                      )}
                      {u.userId !== me.userId && (
                        <button type="button" onClick={() => setToDelete(u)} data-testid={`user-delete-${u.userId}`} aria-label={`Delete account ${u.email}`} className="text-slate-400 transition-colors hover:text-red-700">
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form onSubmit={create} className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="user-create-form">
        <h2 className="text-sm font-extrabold uppercase tracking-[0.14em] text-navy-900">Add a user</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="nu-name" className="text-xs font-bold text-navy-900">Name</label>
            <input id="nu-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="nu-name" className={inputCls} />
          </div>
          <div>
            <label htmlFor="nu-email" className="text-xs font-bold text-navy-900">Email</label>
            <input id="nu-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="nu-email" className={inputCls} />
          </div>
          <div>
            <label htmlFor="nu-role" className="text-xs font-bold text-navy-900">Role</label>
            <select id="nu-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="nu-role" className={inputCls}>
              {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="nu-password" className="text-xs font-bold text-navy-900">Temporary password (min 10 chars)</label>
            <input id="nu-password" type="text" required minLength={10} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="nu-password" className={inputCls} autoComplete="off" />
          </div>
        </div>
        <button type="submit" disabled={busy} data-testid="nu-create" className="mt-4 inline-flex items-center gap-2 rounded-md bg-navy-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-navy-700 disabled:opacity-50">
          <Plus className="h-4 w-4" aria-hidden="true" /> Create Account
        </button>
      </form>

      <ConfirmDialog
        open={!!toDelete}
        title="Delete this account?"
        message={`The ${toDelete?.role || ''} account for ${toDelete?.email || ''} will be permanently deleted and will no longer be able to sign in. This cannot be undone.`}
        destructive
        confirmLabel="Delete Permanently"
        onCancel={() => setToDelete(null)}
        onConfirm={deleteUser}
      />
    </div>
  );
}
